import {
	RiAlertLine,
	RiCheckLine,
	RiCloseLine,
	RiLoader4Line,
	RiPlayLine,
	RiRefreshLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/catalogue/import")({
	component: ProhandelPage,
});

// ---------------------------------------------------------------------------
// Shared types + helpers (sync jobs)
// ---------------------------------------------------------------------------

interface SyncJob {
	id: string;
	jobType: string;
	status: string;
	startedAt: string;
	finishedAt?: string;
	itemsProcessed: number;
	itemsFailed: number;
	errorMessage?: string;
	details?: {
		phase?: string;
		processed?: number;
		failed?: number;
	};
}

const PHASE_LABELS: Record<string, string> = {
	fetching_lookups: "Fetching lookups",
	syncing_articles: "Syncing articles",
	syncing_sizes: "Syncing sizes",
	fetching_changed_articles: "Fetching changed articles",
	fetching_changed_sizes: "Fetching changed sizes",
	upserting_articles: "Saving articles",
	upserting_sizes: "Saving sizes",
	fetching_in_stock_sizes: "Scanning in-stock sizes",
	syncing_in_stock_articles: "Syncing in-stock articles",
	upserting_in_stock_sizes: "Saving in-stock sizes",
	rebuilding_catalogue: "Rebuilding catalogue",
};

function formatPhase(phase?: string): string {
	if (!phase) return "Starting";
	return PHASE_LABELS[phase] ?? phase;
}

function formatJobType(type: string): string {
	if (type === "prohandel_full") return "Full";
	if (type === "prohandel_delta") return "Delta";
	if (type === "prohandel_stock_only") return "In-stock (legacy)";
	if (type === "prohandel_fast_in_stock") return "Fast in-stock";
	return type;
}

function formatDuration(startedAt: string, finishedAt?: string): string {
	const start = new Date(startedAt).getTime();
	const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
	const ms = end - start;
	if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
	if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
	const h = Math.floor(ms / 3_600_000);
	const m = Math.round((ms % 3_600_000) / 60_000);
	return `${h}h ${m}m`;
}

function StatusBadge({ status }: { status: string }) {
	if (status === "running")
		return (
			<Badge variant="default" className="gap-1">
				<RiLoader4Line className="size-3 animate-spin" /> running
			</Badge>
		);
	if (status === "completed")
		return (
			<Badge
				variant="secondary"
				className="gap-1 bg-green-500/15 text-green-700 dark:text-green-300"
			>
				<RiCheckLine className="size-3" /> completed
			</Badge>
		);
	if (status === "failed")
		return (
			<Badge variant="destructive" className="gap-1">
				<RiCloseLine className="size-3" /> failed
			</Badge>
		);
	return <Badge variant="outline">{status}</Badge>;
}

// ---------------------------------------------------------------------------
// Sync polling hook
// ---------------------------------------------------------------------------

function useSyncPolling(onComplete: () => void) {
	const [activeJob, setActiveJob] = useState<SyncJob | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const startPolling = useCallback(() => {
		const poll = async () => {
			try {
				const { data } = await fetchClient.GET("/api/v1/prohandel/sync/jobs", {
					params: { query: { limit: 1, offset: 0 } },
				});
				const jobs = (data as { data?: SyncJob[] })?.data ?? [];
				const latest = jobs[0];
				if (!latest) return;

				if (latest.status === "running") {
					setActiveJob(latest);
				} else {
					setActiveJob(null);
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
					}
					if (latest.status === "completed") {
						toast.success(
							`Sync completed: ${latest.itemsProcessed} processed${
								latest.itemsFailed > 0 ? `, ${latest.itemsFailed} failed` : ""
							}`,
						);
					} else if (latest.status === "failed") {
						toast.error(`Sync failed: ${latest.errorMessage ?? "unknown"}`);
					}
					onComplete();
				}
			} catch {}
		};
		poll();
		intervalRef.current = setInterval(poll, 2000);
	}, [onComplete]);

	useEffect(() => {
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, []);

	return { activeJob, startPolling };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ProhandelPage() {
	const queryClient = useQueryClient();
	const [confirmFullSync, setConfirmFullSync] = useState(false);

	const invalidate = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/prohandel/sync/jobs"],
		});
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/prohandel/sync/status"],
		});
	}, [queryClient]);

	const { activeJob, startPolling } = useSyncPolling(invalidate);

	const { data: jobsRaw, isLoading } = $api.useQuery("get", "/api/v1/prohandel/sync/jobs", {
		params: { query: { limit: 30, offset: 0 } },
	});

	const jobs = ((jobsRaw as { data?: SyncJob[] })?.data ?? []) as SyncJob[];

	const { data: statusRaw } = $api.useQuery("get", "/api/v1/prohandel/sync/status");
	const syncStatus = Array.isArray(statusRaw)
		? (statusRaw as Array<{ resourceName?: string; lastSyncedAt?: string }>)
		: [];
	// Every sync mode writes the "article" row, including `delta`, so this is
	// the last DELTA, not proof that stock is current. Labelled accordingly -
	// it used to read "Last successful sync", which is green for a warm org
	// running delta-only forever while its stock ages without bound.
	const lastDeltaAt = syncStatus.find((s) => s.resourceName === "article")?.lastSyncedAt;

	// The full in-stock walk is the ONLY signal that can detect a depletion
	// (Prohandel's changed-feed never reports one), so its age is the number
	// that actually says whether what we publish is real.
	const { data: fullWalk } = $api.useQuery("get", "/api/v1/prohandel/sync/full-walk" as never);
	const walk = fullWalk as
		| {
				lastFullWalkAt?: string;
				intervalHours?: number;
				ageHours?: number;
				overdue?: boolean;
		  }
		| undefined;

	async function triggerSync(mode: "delta" | "full" | "stock_only" | "fast_in_stock") {
		try {
			const { error } = await fetchClient.POST("/api/v1/prohandel/sync", {
				params: { query: { mode } },
			});
			if (error) {
				toast.error("Failed to start sync");
				return;
			}
			const label =
				mode === "full"
					? "Full"
					: mode === "stock_only"
						? "In-stock (legacy)"
						: mode === "fast_in_stock"
							? "Fast in-stock"
							: "Delta";
			toast.info(`${label} sync started`);
			setTimeout(startPolling, 500);
		} catch {
			toast.error("Failed to start sync");
		}
	}

	const syncing = activeJob !== null;
	const syncElapsed = activeJob
		? Math.round((Date.now() - new Date(activeJob.startedAt).getTime()) / 1000)
		: 0;

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Prohandel</h1>
					<p className="text-sm text-muted-foreground">
						Import log and sync controls.
						{lastDeltaAt && <> Last delta: {new Date(lastDeltaAt).toLocaleString()}</>}
					</p>
					{/* The full walk is the page's primary freshness signal. "Last
					    delta" above is minutes old for a warm org whether or not
					    stock is current, so it cannot carry this meaning. */}
					{walk && (
						<div className="mt-2 flex items-center gap-2" data-testid="full-walk-status">
							<span className="text-sm text-muted-foreground">Last full stock walk:</span>
							<span className="text-sm">
								{walk.lastFullWalkAt ? new Date(walk.lastFullWalkAt).toLocaleString() : "never"}
							</span>
							<Badge variant={walk.overdue ? "destructive" : "secondary"}>
								{walk.overdue
									? walk.ageHours === undefined
										? "never run"
										: `overdue (${walk.ageHours}h)`
									: `${walk.ageHours ?? 0}h ago`}
							</Badge>
						</div>
					)}
					{walk?.overdue && (
						<p className="mt-1 text-xs text-destructive">
							Stock depletions are only detected by the full walk. Until it runs, sold-out items can
							still be published to Farfetch.
						</p>
					)}
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" disabled={syncing}>
							{syncing ? (
								<RiLoader4Line className="size-4 mr-1.5 animate-spin" />
							) : (
								<RiRefreshLine className="size-4 mr-1.5" />
							)}
							{syncing ? "Syncing..." : "Sync"}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => triggerSync("fast_in_stock")}>
							Fast in-stock
							<span className="ml-auto text-xs text-muted-foreground">recommended</span>
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => triggerSync("delta")}>
							Delta sync
							<span className="ml-auto text-xs text-muted-foreground">since last</span>
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => triggerSync("stock_only")}>
							In-stock (legacy)
							<span className="ml-auto text-xs text-muted-foreground">slow</span>
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setConfirmFullSync(true)}>
							Full resync
							<span className="ml-auto text-xs text-muted-foreground">everything</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Active job banner */}
			{syncing && activeJob && (
				<Card className="border-blue-500/30 bg-blue-500/5">
					<CardContent className="py-3 flex items-center gap-3">
						<RiLoader4Line className="size-4 animate-spin text-blue-500 shrink-0" />
						<div className="text-sm flex-1">
							<span className="font-medium">{formatJobType(activeJob.jobType)} sync</span>
							<span className="text-muted-foreground">
								{" — "}
								{formatPhase(activeJob.details?.phase)}
								{activeJob.details?.processed ? (
									<>
										{" — "}
										{activeJob.details.processed.toLocaleString()} processed
									</>
								) : null}
								{activeJob.details?.failed ? (
									<>, {activeJob.details.failed.toLocaleString()} failed</>
								) : null}
								{" — "}
								{syncElapsed}s
							</span>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Full resync confirm */}
			<AlertDialog open={confirmFullSync} onOpenChange={setConfirmFullSync}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Full resync?</AlertDialogTitle>
						<AlertDialogDescription>
							Re-imports every article. Can take hours. Prefer "In-stock only" unless you need
							archived items.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setConfirmFullSync(false);
								triggerSync("full");
							}}
						>
							Start full resync
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* History table */}
			<Card>
				<CardContent className="p-0">
					{isLoading ? (
						<div className="p-6 space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<Skeleton key={i} className="h-12 w-full" />
							))}
						</div>
					) : jobs.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
							<RiPlayLine className="size-10 mb-3 opacity-50" />
							<p className="text-sm">No syncs yet</p>
							<p className="text-xs mt-1">Trigger a sync from the button above.</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Started</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Duration</TableHead>
									<TableHead>Phase</TableHead>
									<TableHead className="text-right">Processed</TableHead>
									<TableHead className="text-right">Failed</TableHead>
									<TableHead>Error</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{jobs.map((j) => (
									<TableRow key={j.id}>
										<TableCell className="whitespace-nowrap text-xs">
											{new Date(j.startedAt).toLocaleString()}
										</TableCell>
										<TableCell>{formatJobType(j.jobType)}</TableCell>
										<TableCell>
											<StatusBadge status={j.status} />
										</TableCell>
										<TableCell className="text-xs">
											{formatDuration(j.startedAt, j.finishedAt)}
										</TableCell>
										<TableCell className="text-xs text-muted-foreground">
											{formatPhase(j.details?.phase)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{j.itemsProcessed.toLocaleString()}
										</TableCell>
										<TableCell
											className={cn("text-right tabular-nums", j.itemsFailed > 0 && "text-red-600")}
										>
											{j.itemsFailed.toLocaleString()}
										</TableCell>
										<TableCell className="max-w-[280px]">
											{j.errorMessage && (
												<span className="inline-flex items-start gap-1 text-xs text-destructive">
													<RiAlertLine className="size-3 mt-0.5 shrink-0" />
													<span className="truncate" title={j.errorMessage}>
														{j.errorMessage}
													</span>
												</span>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
